// ── Elevation × slope rules, extracted VERBATIM from the old Cognito DD form ─────────────────────
// Source: https://www.cognitoforms.com/SamuiLawFirmCoLtd/DueDiligence, form-def model, 2026-08-13.
// The owner's instruction is exact parity ("新系统做DD需要和老系统一样"), so the texts below are the
// old form's own wording — including its quirks ("buidling", "they are:" in lower case). Do not
// polish them without the owner's say-so: these sentences have been going out on issued reports for
// years and the firm's lawyers know them by heart.
//
// The old form held SIX full summary variants (_p12.._p17), one per elevation×slope pair:
//   Zone 2 (0-80m)      × <35°  → no additional restrictions
//   Zone 2 (0-80m)      × >35°  → conditions a)–f)
//   Zone 3.1 (80-140m)  × <35°  → "may apply" a)–e)   (connector "but,")
//   Zone 3.1 (80-140m)  × >35°  → "may apply" a)–i)   (connector "and,")
//   Zone 3.2 (>140m)    × <35°  → conditions a)–h)
//   Zone 3.2 (>140m)    × >35°  → conditions a)–i)
// Zone 1 (sea level) had NO variant: the old form showed no elevation paragraph at all, and the
// owner chose to keep that behaviour.

export type ElevKey = '0-80' | '80-140' | '140+' | '';
export type SlopeKey = 'lt35' | 'gt35' | '';

export const elevationKey = (v: any): ElevKey => {
  const s = String(v || '');
  if (s.includes('between 0-80m')) return '0-80';
  if (s.includes('between 80-140m')) return '80-140';
  if (s.includes('greater than 140')) return '140+';
  return '';                                    // Zone 1 (sea level), or nothing chosen yet
};
export const slopeKey = (v: any): SlopeKey => {
  const s = String(v || '');
  if (s.includes('less than 35')) return 'lt35';
  if (s.includes('greater than 35')) return 'gt35';
  return '';
};

export type ElevationVariant = {
  elev: ElevKey; slope: SlopeKey;
  /** the word joining the two bolded selections: "and" / "but," / "and," — old form varies it */
  connector: string;
  /** sentence after the selections; conditions (if any) follow as an indented a)-i) list */
  intro: string;
  conditions: string[];
};

const MAY_APPLY = 'there are some additional restrictions that may apply to the zoning, maximum usage and/or suitability of the land. they are:';
const THESE_ARE = 'there are some additional restrictions to the standard zoning regulations, maximum usage and/or suitability of the land. These conditions are:';

export const ELEVATION_VARIANTS: ElevationVariant[] = [
  {
    elev: '0-80', slope: 'lt35', connector: 'and',
    intro: 'there should not be any additional restrictions to the standard zoning regulations, maximum usage and/or suitability of the land.',
    conditions: [],
  },
  {
    elev: '0-80', slope: 'gt35', connector: 'and', intro: THESE_ARE,
    conditions: [
      'a single dwelling only is permitted and;',
      'building cannot exceed 6m in height and;',
      'no more than 25% of the land can be built upon and;',
      '50% of land must contain native plants/trees and greenery and;',
      'a minimum plot size of 480m2 is required and;',
      'if the slope is greater than 50 degrees then, construction on the plot is prohibited.',
    ],
  },
  {
    elev: '80-140', slope: 'lt35', connector: 'but,', intro: MAY_APPLY,
    conditions: [
      'construction cannot exceed 6m in total height and;',
      'must leave at least 50% of the land vacant or whichever is lower - 50% or the "Maximum Usage" stated above ({MaximumUsage}) and;',
      'must include an approved water drainage system for the prevention of flooding and;',
      'must have a roof consisting of 80% of the total buidling area , and roof color must match natural surroundings and;',
      'a minimum plot size of 400m2, otherwise construction is prohibited.',
    ],
  },
  {
    elev: '80-140', slope: 'gt35', connector: 'and,', intro: MAY_APPLY,
    conditions: [
      'a single dwelling only is permitted and;',
      'building cannot exceed 6m in height and;',
      'construction cannot exceed 80m2 in total enclosed area and;',
      'no more than 25% of the land can be built upon and;',
      '50% of land must contain native plants/trees and greenery and;',
      'a minimum plot size of 480m2 is required otherwise construction is prohibited and;',
      'must include an approved water drainage system for the prevention of flooding and;',
      'must have a roof consisting of 80% of the total buidling area , and roof color must match natural surroundings and;',
      'if the slope of the land is greater than 50 degrees then, construction on the plot is prohibited.',
    ],
  },
  {
    elev: '140+', slope: 'lt35', connector: 'and', intro: THESE_ARE,
    conditions: [
      'a single dwelling only is permitted and;',
      'building cannot exceed 6m in height and;',
      'construction cannot exceed 90m2 in total enclosed area and;',
      'no more than 40% of the land can be built upon and;',
      '50% of land must contain native plants/trees and greenery and;',
      'a minimum plot size of 400m2 is required and;',
      'must include an approved water drainage system for the prevention of flooding and;',
      'must have a roof consisting of 80% of the total buidling area , and roof color must match natural surroundings.',
    ],
  },
  {
    elev: '140+', slope: 'gt35', connector: 'and', intro: THESE_ARE,
    conditions: [
      'a single dwelling only is permitted and;',
      'building cannot exceed 6m in height and;',
      'construction cannot exceed 80m2 in total enclosed area and;',
      'no more than 25% of the land can be built upon and;',
      '50% of land must contain native plants/trees and greenery and;',
      'a minimum plot size of 480m2 is required and;',
      'must include an approved water drainage system for the prevention of flooding and;',
      'must have a roof consisting of 80% of the total buidling area , and roof color must match natural surroundings and;',
      'if the slope is greater than 50 degrees then, construction on the plot is prohibited .',
    ],
  },
];

export function elevationVariant(landElevation: any, slope: any): ElevationVariant | null {
  const e = elevationKey(landElevation), s = slopeKey(slope);
  if (!e || !s) return null;
  return ELEVATION_VARIANTS.find(v => v.elev === e && v.slope === s) || null;
}

/**
 * Plot-too-small IMPORTANT NOTE (old form _p10/_p11): fires only on the two high-elevation bands.
 * Returns the minimum plot size the warning should quote, or 0 when no warning shows.
 */
export function plotTooSmallMin(landElevation: any, slope: any, m2: any): 0 | 400 | 480 {
  const e = elevationKey(landElevation), s = slopeKey(slope);
  if (e !== '80-140' && e !== '140+') return 0;
  const size = Number(m2) || 0;
  if (size <= 0) return 0;
  if (s === 'lt35' && size < 400) return 400;
  if (s === 'gt35' && size < 480) return 480;
  return 0;
}

/** The Land Elevation (Estimate) upload shows only when the elevation names 140m — i.e. Zone 3.1
 *  ("between 80-140m") or Zone 3.2 ("greater than 140m"). Old form: indexOf("140m"). */
export const needsElevationEstimate = (landElevation: any): boolean => String(landElevation || '').includes('140m');
